import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import ModeratorLayout from "@/components/ModeratorLayout/ModeratorLayout";
import { unwrapPaginated } from "@/config/api";
import { EMPTY_VALUE, formatDate } from "@/i18n/format";
import {
  getModeratorsVerificationDocumentsRetrieveQueryKey,
  getModeratorsVerificationRegistrationsRetrieveQueryKey,
  getModeratorsVerificationShareholdersRetrieveQueryKey,
  useModeratorsVerificationDocumentsApproveCreate,
  useModeratorsVerificationDocumentsRejectCreate,
  useModeratorsVerificationDocumentsRetrieve,
  useModeratorsVerificationRegistrationsApproveCreate,
  useModeratorsVerificationRegistrationsRejectCreate,
  useModeratorsVerificationRegistrationsRetrieve,
  useModeratorsVerificationShareholdersRetrieve,
  useModeratorsVerificationShareholdersVerifyCreate,
} from "@/services/moderators/generated/moderators-verification/moderators-verification";
import "./ModeratorPage.css";
import "./ModeratorVerificationPage.css";

type Tab = "documents" | "registrations" | "shareholders";

interface QueueDoc {
  id: number;
  companyId: number;
  companyName: string;
  fileName: string;
  fileFormat: string;
  fileUrl: string;
  status: string;
  createdAt: string;
}

interface QueueReg {
  id: number;
  companyId: number;
  companyName: string;
  country: string;
  registrationNumber: string;
  corporationType: string;
  status: string;
  registeredDate: string;
  createdAt: string;
}

interface QueueShareholder {
  id: number;
  companyId: number;
  companyName: string;
  name: string;
  nationality: string;
  ownershipPercent: string;
  idVerified: "yes" | "no";
  uboStatus: "yes" | "no";
  createdAt: string;
}

const ModeratorVerificationPage = () => (
  <ModeratorLayout>
    <VerificationContent />
  </ModeratorLayout>
);

const VerificationContent = () => {
  const { t } = useTranslation(["moderator", "common"]);
  const [tab, setTab] = useState<Tab>("documents");
  return (
    <div>
      <header className="mod-page__header">
        <div>
          <h1 className="mod-page__title">{t("verification.title")}</h1>
          <p className="mod-page__subtitle">{t("verification.subtitle")}</p>
        </div>
      </header>

      <div className="mod-page__toolbar">
        {(["documents", "registrations", "shareholders"] as const).map((key) => (
          <button
            key={key}
            type="button"
            className={`mod-page__btn ${tab === key ? "mod-page__btn--primary" : "mod-page__btn--ghost"}`}
            onClick={() => setTab(key)}
          >
            {t(`verification.tabs.${key}`)}
          </button>
        ))}
      </div>

      {tab === "documents" ? <DocumentsTab /> : null}
      {tab === "registrations" ? <RegistrationsTab /> : null}
      {tab === "shareholders" ? <ShareholdersTab /> : null}
    </div>
  );
};

const DocumentsTab = () => {
  const { t } = useTranslation(["moderator", "common"]);
  const qc = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<"pending" | "approved" | "rejected" | "all">("pending");
  const params = { status: statusFilter };
  const { data, isLoading, error } = useModeratorsVerificationDocumentsRetrieve(params);
  const items = unwrapPaginated<QueueDoc>(data);
  const invalidate = () =>
    qc.invalidateQueries({ queryKey: getModeratorsVerificationDocumentsRetrieveQueryKey(params) });
  const approve = useModeratorsVerificationDocumentsApproveCreate({ mutation: { onSuccess: invalidate } });
  const reject = useModeratorsVerificationDocumentsRejectCreate({ mutation: { onSuccess: invalidate } });

  return (
    <>
      <div className="mod-page__toolbar">
        <select
          className="mod-page__select"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)}
        >
          <option value="pending">{t("shared.pending")}</option>
          <option value="approved">{t("shared.approved")}</option>
          <option value="rejected">{t("shared.rejected")}</option>
          <option value="all">{t("common:labels.all")}</option>
        </select>
      </div>
      {isLoading ? <div className="mod-page__loading">{t("common:states.loading")}</div> : null}
      {error ? <div className="mod-page__error">{t("verification.documentsLoadError")}</div> : null}
      {!isLoading && items.length === 0 ? <div className="mod-page__empty">{t("verification.empty")}</div> : null}
      {items.map((doc) => (
        <article key={doc.id} className="mod-page__card mod-verify__row">
          <div className="mod-page__card-body">
            <h2 className="mod-page__card-title">
              <a href={doc.fileUrl} target="_blank" rel="noreferrer">
                {doc.fileName}
              </a>
            </h2>
            <p className="mod-page__card-meta">
              <Link to={`/moderator/companies/${doc.companyId}`}>{doc.companyName}</Link> ·{" "}
              {t("shared.docMeta", { format: doc.fileFormat || EMPTY_VALUE, date: formatDate(doc.createdAt) })}{" "}
              <span className={`mod-page__chip ${docChip(doc.status)}`}>{doc.status}</span>
            </p>
          </div>
          {doc.status === "pending" ? (
            <div className="mod-verify__actions">
              <button
                type="button"
                className="mod-page__btn mod-page__btn--primary"
                disabled={approve.isPending && approve.variables?.id === doc.id}
                onClick={() => approve.mutate({ id: doc.id })}
              >
                {t("common:actions.approve")}
              </button>
              <button
                type="button"
                className="mod-page__btn mod-page__btn--danger"
                disabled={reject.isPending && reject.variables?.id === doc.id}
                onClick={() => reject.mutate({ id: doc.id })}
              >
                {t("common:actions.reject")}
              </button>
            </div>
          ) : null}
        </article>
      ))}
    </>
  );
};

const RegistrationsTab = () => {
  const { t } = useTranslation(["moderator", "common"]);
  const qc = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<"pending" | "active" | "inactive" | "all">("pending");
  const params = { status: statusFilter };
  const { data, isLoading, error } = useModeratorsVerificationRegistrationsRetrieve(params);
  const items = unwrapPaginated<QueueReg>(data);
  const invalidate = () =>
    qc.invalidateQueries({ queryKey: getModeratorsVerificationRegistrationsRetrieveQueryKey(params) });
  const approve = useModeratorsVerificationRegistrationsApproveCreate({ mutation: { onSuccess: invalidate } });
  const reject = useModeratorsVerificationRegistrationsRejectCreate({ mutation: { onSuccess: invalidate } });

  return (
    <>
      <div className="mod-page__toolbar">
        <select
          className="mod-page__select"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)}
        >
          <option value="pending">{t("shared.pending")}</option>
          <option value="active">{t("shared.active")}</option>
          <option value="inactive">{t("shared.inactive")}</option>
          <option value="all">{t("common:labels.all")}</option>
        </select>
      </div>
      {isLoading ? <div className="mod-page__loading">{t("common:states.loading")}</div> : null}
      {error ? <div className="mod-page__error">{t("verification.loadError")}</div> : null}
      {!isLoading && items.length === 0 ? <div className="mod-page__empty">{t("verification.empty")}</div> : null}
      {items.map((reg) => (
        <article key={reg.id} className="mod-page__card mod-verify__row">
          <div className="mod-page__card-body">
            <h2 className="mod-page__card-title">
              {reg.country} — {reg.registrationNumber}
            </h2>
            <p className="mod-page__card-meta">
              <Link to={`/moderator/companies/${reg.companyId}`}>{reg.companyName}</Link> ·{" "}
              {reg.corporationType || EMPTY_VALUE} ·{" "}
              <span className={`mod-page__chip ${regChip(reg.status)}`}>{reg.status}</span>
            </p>
          </div>
          {reg.status !== "active" ? (
            <div className="mod-verify__actions">
              <button
                type="button"
                className="mod-page__btn mod-page__btn--primary"
                disabled={approve.isPending && approve.variables?.id === reg.id}
                onClick={() => approve.mutate({ id: reg.id })}
              >
                {t("shared.activate")}
              </button>
              {reg.status !== "inactive" ? (
                <button
                  type="button"
                  className="mod-page__btn mod-page__btn--danger"
                  disabled={reject.isPending && reject.variables?.id === reg.id}
                  onClick={() => reject.mutate({ id: reg.id })}
                >
                  {t("shared.markInactive")}
                </button>
              ) : null}
            </div>
          ) : null}
        </article>
      ))}
    </>
  );
};

const ShareholdersTab = () => {
  const { t } = useTranslation(["moderator", "common"]);
  const qc = useQueryClient();
  const [verifiedFilter, setVerifiedFilter] = useState<"no" | "yes" | "all">("no");
  const params = { verified: verifiedFilter };
  const { data, isLoading, error } = useModeratorsVerificationShareholdersRetrieve(params);
  const items = unwrapPaginated<QueueShareholder>(data);
  const invalidate = () =>
    qc.invalidateQueries({ queryKey: getModeratorsVerificationShareholdersRetrieveQueryKey(params) });
  const verify = useModeratorsVerificationShareholdersVerifyCreate({ mutation: { onSuccess: invalidate } });

  return (
    <>
      <div className="mod-page__toolbar">
        <select
          className="mod-page__select"
          value={verifiedFilter}
          onChange={(e) => setVerifiedFilter(e.target.value as typeof verifiedFilter)}
        >
          <option value="no">{t("verification.unverified")}</option>
          <option value="yes">{t("verification.verified")}</option>
          <option value="all">{t("common:labels.all")}</option>
        </select>
      </div>
      {isLoading ? <div className="mod-page__loading">{t("common:states.loading")}</div> : null}
      {error ? <div className="mod-page__error">{t("verification.loadError")}</div> : null}
      {!isLoading && items.length === 0 ? <div className="mod-page__empty">{t("verification.empty")}</div> : null}
      {items.map((sh) => (
        <article key={sh.id} className="mod-page__card mod-verify__row">
          <div className="mod-page__card-body">
            <h2 className="mod-page__card-title">
              {sh.name} — {sh.ownershipPercent}%
            </h2>
            <p className="mod-page__card-meta">
              <Link to={`/moderator/companies/${sh.companyId}`}>{sh.companyName}</Link> ·{" "}
              {sh.nationality || EMPTY_VALUE} ·{" "}
              <span
                className={`mod-page__chip ${sh.idVerified === "yes" ? "mod-page__chip--success" : "mod-page__chip--warning"}`}
              >
                {sh.idVerified === "yes" ? t("shared.idVerified") : t("shared.idUnverified")}
              </span>{" "}
              <span className="mod-page__chip">{t("shared.ubo", { status: sh.uboStatus })}</span>
            </p>
          </div>
          <div className="mod-verify__actions">
            <button
              type="button"
              className="mod-page__btn mod-page__btn--primary"
              disabled={(verify.isPending && verify.variables?.id === sh.id) || sh.idVerified === "yes"}
              onClick={() => verify.mutate({ id: sh.id, data: { idVerified: "yes" } })}
            >
              {t("shared.markVerified")}
            </button>
            <button
              type="button"
              className="mod-page__btn mod-page__btn--ghost"
              disabled={verify.isPending && verify.variables?.id === sh.id}
              onClick={() => verify.mutate({ id: sh.id, data: { uboStatus: sh.uboStatus === "yes" ? "no" : "yes" } })}
            >
              {t("shared.toggleUbo")}
            </button>
          </div>
        </article>
      ))}
    </>
  );
};

function docChip(status: string): string {
  if (status === "approved") return "mod-page__chip--success";
  if (status === "rejected") return "mod-page__chip--danger";
  return "mod-page__chip--warning";
}

function regChip(status: string): string {
  if (status === "active") return "mod-page__chip--success";
  if (status === "inactive") return "mod-page__chip--danger";
  return "mod-page__chip--warning";
}

export default ModeratorVerificationPage;
